/**
 * Application Entry Point
 *
 * This is the main entry point for the application.
 * It sets up the React application with all necessary providers and configurations.
 */

// ============================================================================
// CRITICAL: Initialize Sentry FIRST for error tracking
// This must be initialized before any other code runs
// ============================================================================
import { initializeSentry } from "./configs/sentry";
initializeSentry();

// ============================================================================
// NOTE: Extension error suppression is handled in public/suppress-extension-errors.js
// which loads before React. We don't override console.error here to avoid breaking
// React DevTools and stack traces. Window-level error handlers are sufficient.
// ============================================================================

import React, { Suspense, lazy } from "react";
import { createRoot } from "react-dom/client";
import { Provider, useSelector } from "react-redux";
import { store } from "./redux/store";
import { QueryClient, QueryClientProvider } from "react-query";
import { ReactQueryDevtools } from "react-query/devtools";
import { ToastContainer, toast, Slide } from "react-toastify";
import { AbilityContext } from "./utility/context/Can";
import { ThemeContext } from "./utility/context/ThemeColors";
import ability from "./configs/acl/ability";
import AxiosInterceptors from "./configs/axios/AxiosInterceptors";
import Spinner from "./@core/components/spinner/Fallback-spinner";
import ErrorBoundary from "./components/common/ErrorBoundary";
import config from "./configs/env";

// Import logger to initialize console filtering based on debug flags
// This must be imported early to intercept console.log calls throughout the app
import "./utils/logger";

// Import service worker registration
import * as serviceWorkerRegistration from "./serviceWorkerRegistration";

// Import styles
import "./@core/assets/fonts/feather/iconfont.css";
import "./@core/scss/core.scss";
import "./assets/scss/style.scss";
import "@styles/react/libs/toastify/toastify.scss";
// REMOVED: react-perfect-scrollbar - now loaded only in components that use it
// REMOVED: prismjs - syntax highlighter not needed globally, lazy load where needed
import "rc-slider/assets/index.css";
import "./styles/realtime-animations.css"; // Real-time table update animations

// Import i18n configuration
import "./configs/i18n";

// Import providers
import { OnlineDataProvider } from "./provider";
import { isUserLoggedIn } from "./utility/Utils";
import { CoinPriceProvider } from "./utility/hooks/coins/usePrice";

// Create React Query client with aggressive optimization configuration
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        // Don't retry on 401 (unauthorized) or 404 (not found)
        if (error?.response?.status === 401 || error?.response?.status === 404) {
          return false;
        }
        // Don't retry on network errors (status 0) - fail fast for better UX
        if (error?.status === 0 || !error?.response) {
          return false;
        }
        return failureCount < 3;
      },
      // More aggressive default cache settings for better performance
      staleTime: 1000 * 60 * 10, // 10 minutes (increased from 5)
      cacheTime: 1000 * 60 * 30, // 30 minutes
      refetchOnWindowFocus: false,
      refetchOnMount: false, // Changed to false to prevent unnecessary refetches
      refetchOnReconnect: false, // Changed to false to prevent reconnect spam
      // Keep previous data while fetching new data for better UX
      keepPreviousData: true,
      // Global error handler for background refetches - suppress network error toasts
      onError: (error) => {
        // Skip error handling if this is a token expiration error
        if (error?._handledByTokenExpiration || error?.status === 401) {
          console.log('✅ React Query: Skipping error handling - token expiration in progress');
          return;
        }

        // For network errors (API down, connection issues), don't show toast
        // This follows UX patterns from major crypto exchanges for silent error handling
        const isNetworkError = error?.status === 0 || !error?.response;
        if (isNetworkError) {
          console.log('🔇 React Query: Suppressing network error toast for background refetch');
          return;
        }

        // For other errors, let individual hooks handle them
        // Don't show global toast here to avoid duplicate toasts
      },
    },
  },
});

// Lazy load the main App component
const LazyApp = lazy(() => import("./App"));

// RTL-aware ToastContainer component
const RTLToastContainer = () => {
  const isRTL = useSelector(state => state.layout.isRTL);

  return (
    <ToastContainer
      position="top-right"
      autoClose={5000}
      hideProgressBar={false}
      newestOnTop
      closeOnClick
      rtl={isRTL}
      pauseOnFocusLoss
      draggable
      pauseOnHover
      limit={3}
      transition={Slide}
    />
  );
};

// Create root component with all providers
const Root = () => (
  <ErrorBoundary name="RootProvider">
    <Provider store={store}>
      <AbilityContext.Provider value={ability}>
        <ThemeContext>
          <QueryClientProvider client={queryClient}>
            <AxiosInterceptors />

            <CoinPriceProvider>
              {/* OnlineDataProvider must be inside CoinPriceProvider to access useCoinPrice() */}
              {isUserLoggedIn() && <OnlineDataProvider />}

              <Suspense fallback={<Spinner />}>
                <LazyApp />
              </Suspense>
            </CoinPriceProvider>

            <RTLToastContainer />

            {/* Only show React Query Devtools in development */}
            {config.isDevelopment && (
              <ReactQueryDevtools initialIsOpen={false} position="bottom-left" />
            )}
          </QueryClientProvider>
        </ThemeContext>
      </AbilityContext.Provider>
    </Provider>
  </ErrorBoundary>
);

// Initialize RTL direction BEFORE React renders (React 18 fix)
// This ensures the dir attribute is set synchronously before any component mounts
const initializeRTL = () => {
  const storedIsRTL = window.localStorage.getItem('isRTL');
  const storedDirection = window.localStorage.getItem('direction');

  // Default to RTL if not set (matching themeConfig)
  const isRTL = storedIsRTL ? JSON.parse(storedIsRTL) : true;
  const direction = storedDirection ? JSON.parse(storedDirection) : 'rtl';

  // Set HTML dir attribute synchronously
  const htmlElement = document.documentElement;
  htmlElement.setAttribute('dir', direction);

  console.log('🌐 RTL initialized:', { isRTL, direction });
};

// Initialize RTL before rendering
initializeRTL();

// Render the application using React 18's createRoot API
const container = document.getElementById("root");
const root = createRoot(container);
root.render(<Root />);

// Register service worker for PWA support
serviceWorkerRegistration.register({
  onSuccess: () => console.log("Service Worker registered successfully"),
  onUpdate: (registration) => {
    console.log("New content is available; please refresh");
    // Dispatch event for update notification
    window.dispatchEvent(new CustomEvent("sw-update-available"));
  },
});

// Log application initialization
// console.log(`${config.appName} v${config.appVersion} initialized in ${config.env} mode`);
